import type { ReactNode } from 'react';

import type { CmsContentTypes } from '@eneco/cms-types/generated';
import { emailValidation } from '@eneco/common/validation/emailRegex';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import { typedYupResolver } from '@eneco/hooks/form';
import { Box, Checkbox, Heading, InputEmail, InputTelephone, Stack, Text } from '@eneco/sparky';
import {
  type DefaultValues,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  type UseFormHandleSubmit,
  type UseFormRegister,
  useForm,
} from 'react-hook-form';
import * as yup from 'yup';

import { readSectionField } from '../../utils/readSectionField';
import { Header } from '../Header/Header';
import { Layout } from '../Layout/Layout';
import { NextButton } from '../NextButton/NextButton';

type TextFieldContent = {
  label?: string;
  placeholder?: string;
  hint?: string | ReactNode;
};

type ContactDetailsFieldProps<TFieldValues extends FieldValues> = {
  register: UseFormRegister<TFieldValues>;
  error?: string;
  field?: TextFieldContent;
};

type ContactDetailsOptInProps<TFieldValues extends FieldValues> = {
  register: UseFormRegister<TFieldValues>;
  title?: string;
  content?: string;
  headingAs?: 'h2' | 'h3';
  checkboxes: {
    name: FieldPath<TFieldValues>;
    label?: string;
  }[];
};

/** Dutch landline or mobile number, 10 digits, optional separator, no country code. */
export const DUTCH_PHONE_PATTERN = /^(?:0[0-57-9]\d{2}[-\s]?\d{6}|0[0-57-9]\d[-\s]?\d{7}|06[-\s]?\d{8})$/;

export const ContactDetailsPhoneField = <TFieldValues extends FieldValues>({
  register,
  error,
  field,
}: ContactDetailsFieldProps<TFieldValues>) => (
  <InputTelephone
    {...register('phoneNumber' as FieldPath<TFieldValues>)}
    label={field?.label ?? ''}
    placeholder={field?.placeholder}
    hint={field?.hint}
    autoComplete="tel"
    error={error}
  />
);

export const ContactDetailsEmailField = <TFieldValues extends FieldValues>({
  register,
  error,
  field,
  note,
  isGrouped = true,
}: ContactDetailsFieldProps<TFieldValues> & { note?: string; isGrouped?: boolean }) => {
  const emailField = (
    <InputEmail
      {...register('emailAddress' as FieldPath<TFieldValues>)}
      label={field?.label ?? ''}
      placeholder={field?.placeholder}
      hint={field?.hint}
      autoComplete="email"
      error={error}
    />
  );

  if (!isGrouped) return emailField;

  return (
    <Stack.Item>
      {emailField}
      {note && (
        <Box paddingTop="4">
          <Text size="BodyS" color="textLowEmphasis">
            <RichText html={note} />
          </Text>
        </Box>
      )}
    </Stack.Item>
  );
};

export const ContactDetailsOptIn = <TFieldValues extends FieldValues>({
  register,
  title,
  content,
  headingAs = 'h3',
  checkboxes,
}: ContactDetailsOptInProps<TFieldValues>) => (
  <>
    <Stack.Item>
      <Stack direction="column" gap="4">
        <Heading as={headingAs} size="XS">
          {title}
        </Heading>
        <RichText html={content} />
      </Stack>
    </Stack.Item>

    <Stack.Item>
      <Stack direction="column" gap="2">
        {checkboxes.map(checkbox => (
          <Checkbox key={checkbox.name} label={checkbox.label ?? ''} {...register(checkbox.name)} />
        ))}
      </Stack>
    </Stack.Item>
  </>
);

type FieldText = {
  value?: string;
};

type FormFieldContent = TextFieldContent & {
  requiredMessage?: string;
  validationMessage?: string;
};

type FormFieldText = {
  value?: FormFieldContent;
};

export type ContactDetailsTextualData = {
  contactDetails?: {
    phoneNumberFormField?: FormFieldText;
    emailAddressFormField?: FormFieldText;
    emailAddressText?: FieldText;
  };
  content?: {
    title?: { value: string };
    text?: FieldText;
    nextStepText?: FieldText;
    notification?: {
      value?: {
        title?: string;
        content?: string;
      };
    };
  };
  optIn?: {
    title?: FieldText;
    content?: FieldText;
    checkBoxProductOffersFirstPartyContent?: FieldText;
  };
};

/**
 * The names the contact steps of the cv ketel koop, heat pump and solar boiler lead flows are authored under: each flow keeps its own, and their
 * generated content types carry the same fields.
 */
export type LeadContactDetailsContentName =
  'CvKetelKoopFlowContactDetailsStep' | 'HeatPumpLeadFlowContactDetailsStep' | 'SolarBoilerLeadFlowContactDetailsStep';

/** A lead flow's contact step content as the CMS layer hands it over, from either CMS. */
export type LeadContactDetailsContent = CmsContentTypes[LeadContactDetailsContentName];

/**
 * The contact step's content as the CMS layer hands it over, from either CMS: the shared contact step's, or a lead
 * flow's. The two name the opt in title differently (`Optin_title`, `optIn_title`), after their Sitecore sections.
 */
export type ContactDetailsContent = CmsContentTypes['ContactDetailsStep'] | LeadContactDetailsContent;

const optInTitleOf = (content: ContactDetailsContent) => {
  if ('Optin_title' in content) return content.Optin_title;
  return 'optIn_title' in content ? content.optIn_title : undefined;
};

/**
 * What the contact form reads: from the step's flat content when a step passes it, else from Sitecore's sections.
 * The opt-in text is the one field Sitecore keeps in its section (`optIn`), beside a section of the same name.
 */
const contactDetailsTexts = (textualData?: ContactDetailsTextualData, content?: ContactDetailsContent) =>
  content
    ? {
        phoneField: content.phoneNumberFormField,
        emailField: content.emailAddressFormField,
        emailNote: content.emailAddressText,
        optInTitle: optInTitleOf(content),
        optInContent: readSectionField<string>(content, 'optIn', 'content'),
        optInCheckboxLabel: content.checkBoxProductOffersFirstPartyContent,
      }
    : {
        phoneField: textualData?.contactDetails?.phoneNumberFormField?.value,
        emailField: textualData?.contactDetails?.emailAddressFormField?.value,
        emailNote: textualData?.contactDetails?.emailAddressText?.value,
        optInTitle: textualData?.optIn?.title?.value,
        optInContent: textualData?.optIn?.content?.value,
        optInCheckboxLabel: textualData?.optIn?.checkBoxProductOffersFirstPartyContent?.value,
      };

export type ContactDetailsFormValues = {
  phoneNumber: string;
  emailAddress?: string;
  C1?: boolean;
};

export const useContactDetailsForm = <TFieldValues extends ContactDetailsFormValues>(
  textualData: ContactDetailsTextualData | undefined,
  defaultValues: { phoneNumber?: string; emailAddress?: string; C1?: boolean },
  content?: ContactDetailsContent,
) => {
  const { phoneField, emailField } = contactDetailsTexts(textualData, content);

  const formSchema = yup.object({
    phoneNumber: yup
      .string()
      .required(phoneField?.requiredMessage)
      .matches(DUTCH_PHONE_PATTERN, phoneField?.validationMessage),
    emailAddress: emailValidation({
      emailMessage: emailField?.validationMessage,
      requiredMessage: emailField?.requiredMessage,
    }),
    C1: yup.boolean(),
  });

  return useForm<TFieldValues>({
    mode: 'onSubmit',
    defaultValues: defaultValues as DefaultValues<TFieldValues>,
    resolver: typedYupResolver<TFieldValues>(formSchema),
  });
};

const fieldErrorMessage = (error?: { message?: unknown }) =>
  typeof error?.message === 'string' ? error.message : undefined;

export const ContactDetailsFormFields = <TFieldValues extends ContactDetailsFormValues>({
  register,
  errors,
  textualData,
  content,
  showOptIn = true,
}: {
  register: UseFormRegister<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  textualData?: ContactDetailsTextualData;
  /** A step that reads flat content passes it here instead of `textualData`. */
  content?: ContactDetailsContent;
  showOptIn?: boolean;
}) => {
  const texts = contactDetailsTexts(textualData, content);

  return (
    <>
      <ContactDetailsPhoneField
        register={register}
        error={fieldErrorMessage(errors.phoneNumber)}
        field={texts.phoneField}
      />
      <ContactDetailsEmailField
        register={register}
        error={fieldErrorMessage(errors.emailAddress)}
        field={texts.emailField}
        note={texts.emailNote}
      />
      {showOptIn && (
        <ContactDetailsOptIn
          register={register}
          title={texts.optInTitle}
          content={texts.optInContent}
          checkboxes={[{ name: 'C1' as FieldPath<TFieldValues>, label: texts.optInCheckboxLabel }]}
        />
      )}
    </>
  );
};

export const SALESFORCE_LEAD_RECORD_TYPE_ID = '0125I000000cKH0QAM';

export const createSalesforceLeadContact = ({
  firstName,
  surnamePreposition,
  salutation,
  optIn,
  surname,
  postalCode,
  phoneNumber,
  emailAddress,
  houseNumber,
  houseNumberSuffix,
}: {
  firstName?: string;
  surnamePreposition?: string;
  salutation?: string;
  optIn?: boolean;
  surname?: string;
  postalCode?: string;
  phoneNumber?: string;
  emailAddress?: string;
  houseNumber?: string | number;
  houseNumberSuffix?: string | null;
}) => ({
  FirstName__c: firstName ?? '',
  Prefix__c: surnamePreposition ?? '',
  Gender__c: salutation === 'mevr' ? 'v' : 'm',
  Email_opt_in__c: optIn ? 'true' : 'false',
  LastName__c: surname ?? '',
  Postal_Code__c: postalCode ?? '',
  SuppliedPhone: phoneNumber,
  SuppliedEmail: emailAddress ?? '',
  HouseNo__c: houseNumber?.toString() ?? '',
  HouseNoAdd__c: houseNumberSuffix || '',
  RecordTypeId: SALESFORCE_LEAD_RECORD_TYPE_ID,
});

export const LeadContactDetailsShell = <TFieldValues extends FieldValues>({
  content,
  handleSubmit,
  hasError,
  isLoading,
  children,
}: {
  content?: LeadContactDetailsContent;
  handleSubmit: UseFormHandleSubmit<TFieldValues>;
  hasError: boolean;
  isLoading: boolean;
  children: ReactNode;
}) => (
  <Layout.Content variant="A" handleSubmit={handleSubmit as UseFormHandleSubmit<FieldValues>}>
    <Header>
      <Header.Title>{content?.content_title}</Header.Title>
      {content?.text && <Header.Text>{content.text}</Header.Text>}
    </Header>
    {children}
    {hasError && (
      <TrackedNotificationBox
        isAlert
        variant="error"
        title={content?.notification?.title ?? ''}
        text={<RichText html={content?.notification?.content} />}
      />
    )}
    <NextButton isLoading={isLoading}>{content?.nextStepText}</NextButton>
  </Layout.Content>
);
